import { Timestamp } from "firebase/firestore";
import Stripe from "stripe";

export type PlanTier = "free" | "plus" | "max";

// Role-based access control
export type UserRole = "admin" | "affiliate";

export type UserData = {
  uid: string;
  email: string | null;
  displayName: string | null;
  planTier: PlanTier;
  remainingGenerations: number;
  projectsCount: number;
  maxProjects: number;
  shippingAddresses?: ShippingAddress[];
  paymentMethods?: PaymentMethod[];
  createdAt?: Date;
  updatedAt?: Date;
  isAdmin?: boolean; // Legacy - use roles array instead
  roles?: UserRole[]; // RBAC roles
  // Affiliate referral tracking
  referredByAffiliateId?: string;
  referredByCode?: string;
  referralConvertedAt?: Timestamp;
  hasCompletedFirstPurchase?: boolean;
  // If user is an affiliate
  affiliateId?: string;
};

/**
 * Check if a user has a specific role
 * Handles both legacy isAdmin flag and new roles array
 */
export function hasRole(userData: UserData | null | undefined, role: UserRole): boolean {
  if (!userData) return false;

  // Check roles array first
  if (userData.roles?.includes(role)) return true;

  // Fallback to legacy isAdmin flag for admin role
  if (role === "admin" && userData.isAdmin) return true;

  // For affiliate role, also check if they have an affiliateId
  if (role === "affiliate" && userData.affiliateId) return true;

  return false;
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(userData: UserData | null | undefined, roles: UserRole[]): boolean {
  return roles.some(role => hasRole(userData, role));
}

// Affiliate System Types
export type AffiliateStatus = "pending" | "approved" | "active" | "suspended" | "rejected";
export type PayoutStatus = "pending" | "processing" | "completed" | "failed";
export type ReferralStatus = "clicked" | "signed_up" | "converted";

export interface Affiliate {
  id: string;
  userId: string;
  email: string;
  displayName: string | null;
  affiliateCode: string;
  status: AffiliateStatus;
  stripeConnectAccountId?: string;
  stripeConnectOnboardingComplete: boolean;
  commissionRate: number; // 0.10 = 10%
  totalReferrals: number;
  totalConversions: number;
  totalEarnings: number; // cents
  pendingEarnings: number; // cents
  paidEarnings: number; // cents
  applicationNote?: string;
  website?: string;
  socialMedia?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  approvedAt?: Timestamp;
  approvedBy?: string;
}

export interface Referral {
  id: string;
  affiliateId: string;
  affiliateCode: string;
  referredUserId: string;
  referredUserEmail: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  landingPage?: string;
  status: ReferralStatus;
  clickedAt: Timestamp;
  signedUpAt?: Timestamp;
  convertedAt?: Timestamp;
  orderId?: string;
  orderAmount?: number; // cents
  commissionAmount?: number; // cents
  commissionRate?: number;
}

export interface AffiliatePayout {
  id: string;
  affiliateId: string;
  amount: number; // cents
  status: PayoutStatus;
  stripeTransferId?: string;
  periodStart: Timestamp;
  periodEnd: Timestamp;
  referralIds: string[];
  requestedAt: Timestamp;
  processedAt?: Timestamp;
  completedAt?: Timestamp;
  errorMessage?: string;
}

// Prompt versioning interfaces
export interface PromptVersion {
  version: number;
  prompt: string;
  createdAt: Timestamp;
  createdBy?: string; // User who created this version
  performance?: {
    successRate?: number; // Percentage of successful generations
    userRating?: number; // Average user rating (1-5)
    generationCount?: number; // How many times this version was used
  };
  notes?: string; // Optional notes about this version
}

export interface VersionedPrompt {
  versions: PromptVersion[];
  currentVersion: number;
}

// Gemini aspect ratio options with resolution support
// All aspect ratios use 2K resolution (middle column)
export type GeminiAspectRatio =
  | "1:1" // 2048x2048
  | "2:3" // 1696x2528
  | "3:2" // 2528x1696
  | "3:4" // 1792x2400
  | "4:3" // 2400x1792
  | "4:5" // 1856x2304
  | "5:4" // 2304x1856
  | "9:16" // 1536x2752
  | "16:9" // 2752x1536
  | "21:9"; // 3168x1344

// New Responses API types used on the client (mirrors functions/src/responses.ts)
export interface PartialImage {
  index: number;
  imageUrl: string;
  timestamp: Timestamp;
}

// Image attachment types for unified conversational flow
export type AttachmentSource = "upload" | "paste" | "library";

export interface ImageAttachment {
  id: string;
  url: string; // Firebase Storage URL or blob URL for preview
  source: AttachmentSource;
  thumbnailUrl?: string; // For library images
  fileName?: string; // For uploads
}

// Maximum number of attachments allowed per message
export const MAX_ATTACHMENTS = 5;

export interface ImageGeneration {
  id: string;
  conversationId: string;
  prompt: string;
  partialImages: PartialImage[];
  finalImageUrl: string;
  responseId: string;
  parentGenerationId?: string;
  iterationNumber: number;
  createdAt: Timestamp;
  // User-provided reference images for this generation
  attachedImages?: ImageAttachment[];
}

export interface ImageConversation {
  id: string;
  userId: string;
  projectId: string;
  responseId: string;
  status: "active" | "completed" | "error";
  generations: ImageGeneration[];
  userPrompt?: string; // Store user's prompt for enhancement flows
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface PrintDesign {
  id?: string; // Unique identifier for this print design
  name: string;
  description?: string;
  width: number;
  height: number;
  position?: PositionOption;
  printifyPosition?: string; // Printify position name: "front", "back", "left_sleeve", etc.
  // Legacy prompt fields (for backward compatibility)
  prompt?: string; // Optional AI prompt template for this design
  editPrompt?: string; // Optional AI edit prompt template for this design
  // New versioned prompt fields
  promptVersions?: VersionedPrompt; // Versioned AI prompt templates
  editPromptVersions?: VersionedPrompt; // Versioned AI edit prompt templates
  // Image generation configuration
  aspectRatio?: GeminiAspectRatio; // Aspect ratio (defaults to "1:1" if not specified)
  forceTransparent?: boolean; // Force transparent background (OpenAI only for now)
  // Legacy field for backward compatibility
  imageSize?: "square" | "portrait" | "landscape";
  // Variant filtering (for products where print design determines variant options)
  // e.g., tote bags where size is chosen via print design: { size: "13\" × 13\"" }
  // Empty object or undefined = no filtering (show all variants)
  variantFilter?: { [key: string]: string };
}

export interface StickersByAiProject {
  id: string;
  userId: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  // Conversations embedded on the project (replaces generations)
  conversations: ImageConversation[];
  // New image conversation structure for the Responses API
  imageConversation?: ImageConversation;
  currentConversationId?: string; // active conversation to continue
  currentResponseId?: string; // latest OpenAI response id for chaining
  // Current final image for convenience access in UI
  imageUrl?: string;
  selectedProduct?: Product;
  selectedVariant?: ProductVariant; // Template variant (for dimensions/alignment)
  selectedPrintDesign?: PrintDesign;
  printifyData?: PrintifyData;
  paymentId?: string;
  orderIds?: string[];
  alignmentData?: AlignmentData;
  ttl?: Timestamp;
  // Social sharing
  isPublic?: boolean; // When true, design is viewable at /designs/:projectId
  // For products with deferred variant selection
  // Stores user's final size/color choices from Step 4
  finalVariantOptions?: {
    size?: string;
    color?: string;
    [key: string]: string | undefined;
  };
}

export interface UserLibrary {
  userId: string;
  products: StickersByAiProject[];
  updatedAt: Timestamp;
}

export interface PrintifyData {
  generationId: string;
  printifyImageId: string;
  printifyProductId: string;
  printifyVariantId: string;
  mockupImages: any[];
}

export interface StripeData {
  productId: string;
  priceId: string;
}

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AlignmentData {
  cropX: number;
  cropY: number;
  zoom: number;
  rotation: number;
  croppedAreaPixels: CropArea | null;
  croppedImage: string | null;
}

export interface CartItem {
  id: string;
  projectId: string;
  generationId: string;
  quantity: number;
  price: number;
  stripeProductId: string;
  stripePriceId: string;
  printifyProductId: string;
  printifyVariantId: number;
  displayName: string;
  userDescription: string;
  imageUrl: string;
  createdAt: Timestamp;
  shippingFirst: number;
  shippingEach: number;
}

export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  planTier: PlanTier;
  remainingGenerations: number;
  projectsCount: number;
  maxProjects: number;
  shippingAddresses?: ShippingAddress[];
  paymentMethods?: PaymentMethod[];
}

export interface ShippingAddress {
  id: string;
  fullName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;
  isDefault: boolean;
}

export interface PaymentMethod {
  id: string;
  type: "card";
  last4: string;
  brand: string;
  expiryMonth: number;
  expiryYear: number;
  isDefault: boolean;
}

export interface PlanDetails {
  tier: PlanTier;
  name: string;
  price: number;
  maxGenerations: number;
  maxProjects: number;
  features: string[];
}

export type PrintifyShippingAddress = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  country: string;
  region: string;
  address1: string;
  address2: string;
  city: string;
  zip: string;
};

export type PrintifyLineItem = {
  product_id: string;
  variant_id: number;
  quantity: number;
  price: number;
  name: string;
  image_url: string;
};

export type PrintifyOrderRequest = {
  external_id: string;
  label: string;
  line_items: PrintifyLineItem[];
  shipping_method: number;
  send_shipping_notification: boolean;
  address_to: PrintifyShippingAddress;
};

export type StickersByAiOrder = {
  id: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  cartItems: CartItem[];
  stripe: {
    sessionId: string;
    lineItems: Stripe.LineItem[];
    paymentStatus: "pending" | "processing" | "completed" | "failed";
    currency: string;
    amountTotal: number;
  };
  printify: {
    orderId?: string;
    orderRequest: Partial<PrintifyOrderRequest>;
    fulfillmentStatus: "unstarted" | "pending" | "processing" | "completed" | "failed";
  };
  shipping?: {
    carrier: string;
    trackingNumber: string;
    shippedAt: string;
  };
  imageCredits?: {
    quantity: number;
    creditsPerUnit: number;
    totalCredits: number;
  };
  appliedCoupon?: AppliedCoupon;
  orderStatus: "created" | "paid" | "processing" | "completed" | "failed";
  messages: string[];
};

export interface PositionOption {
  id: string;
  xOffset: number;
  yOffset: number;
  cropWidth?: number;
  cropHeight?: number;
}

export interface ProductVariant {
  name: string;
  description: string;
  shape: "circle" | "rectangle";
  shippingEach: number;
  shippingFirst: number;
  stripeProductId: string;
  stripePriceId: string;
  stripePrice: number;

  // NEW: Physical product options (size, color, etc.)
  options?: {
    size?: string; // e.g., "S", "M", "L", "XL", "2XL"
    color?: string; // e.g., "Black", "White", "Heather Navy"
    [key: string]: string | undefined; // Allow other custom options
  };

  // LEGACY: printDesigns at variant level (for backward compatibility)
  // New products should use product-level printDesigns instead
  printDesigns?: PrintDesign[];

  blueprintId: number; // Printify blueprint ID
  variantId: number; // Printify variant ID
  printifyPrintProviderId: number; // Printify print provider ID
  prompt?: string; // Optional AI prompt template for this variant
  editPrompt?: string; // Optional AI edit prompt template for this variant
}

// Coupon/Promo Code Types
export type CouponStatus = "active" | "disabled" | "archived";
export type DiscountType = "percentage" | "fixed";

export interface Coupon {
  id: string;
  code: string; // Customer-facing promo code (stored uppercase)
  discountType: DiscountType;
  discountValue: number; // Percentage (0-100) or fixed amount in cents
  maxRedemptions?: number; // null/undefined = unlimited
  timesRedeemed: number;
  expiresAt?: Timestamp; // null/undefined = no expiration
  minOrderAmount?: number; // Minimum order subtotal in cents
  status: CouponStatus;
  stripeCouponId: string; // Stripe coupon object ID
  stripePromotionCodeId: string; // Stripe promotion code object ID
  createdBy: string; // Admin user ID
  createdAt: Timestamp;
  updatedAt: Timestamp;
  notes?: string;
}

export interface AppliedCoupon {
  code: string;
  discountType: DiscountType;
  discountValue: number;
  discountAmount: number; // Calculated discount in cents
  couponId: string;
}

export interface Product {
  id: string; // generated with generateId()
  name: string;
  description: string; // required
  thumbnail: string; // required, URL to storage
  categories?: string[]; // product categories (supports multiple)

  // NEW: Product-level print designs (shared across all variants)
  // This is the preferred approach for products where all variants share the same print areas
  // (e.g., Hoodie with different colors/sizes but same front/back/sleeve positions)
  printDesigns?: PrintDesign[];

  // Printify configuration (shared by all variants)
  blueprintId?: number; // Printify blueprint ID (if shared across variants)

  // Deferred variant selection (for products with many size/color combinations)
  // If true, user selects size/color in Step 4 instead of Step 1
  deferVariantSelection?: boolean;
  // Which variant to use as template for design dimensions/alignment (default: 0)
  templateVariantIndex?: number;
  // Optional size guide image URL to help users choose the right size
  sizeGuideUrl?: string;

  // SEO fields (for product landing pages)
  slug?: string; // URL-friendly identifier (auto-generated from name if not provided)
  metaTitle?: string; // Custom SEO title (defaults to name if not provided)
  metaDescription?: string; // Custom SEO description (defaults to description if not provided)
  ogImage?: string; // Custom Open Graph image URL (defaults to thumbnail if not provided)
  exampleImages?: string[]; // Array of Firebase Storage URLs for example designs
  mostPopular?: boolean; // Flag to show "Most Popular" badge in product selection

  variants: ProductVariant[];
  sortOrder: number; // required for ordering products
  active?: boolean;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}
